<template>
  <span
    class="app-tooltip__trigger"
    @mouseenter="open"
    @mouseleave="close"
    @focusin="open"
    @focusout="close"
  >
    <slot />

    <!--
      Teleported to <body> so the floating tip is never clipped by an ancestor's
      overflow:hidden and always wins the stacking order. position:fixed + coords
      from the trigger's bounding rect keep it anchored to the trigger.

      v-show, not v-if: vue2-teleport captures its nodes once at mount and only
      moves those back on destroy. A v-if tip is created later, lands in <body>
      untracked, and stays stuck on screen when the trigger unmounts mid-hover.
    -->
    <c-teleport to="body">
      <transition name="app-tooltip-fade">
        <span
          v-show="visible && !disabled && label"
          class="app-tooltip"
          :class="`app-tooltip--${placement}`"
          :style="tipStyle"
          role="tooltip"
        >
          <slot name="content">{{ text }}</slot>
        </span>
      </transition>
    </c-teleport>
  </span>
</template>

<script>
/**
 * Lightweight, dependency-free hover/focus tooltip.
 *
 * Usage:
 *   <app-tooltip text="Approved" placement="top">
 *     <span class="dot"></span>
 *   </app-tooltip>
 *
 * Or with rich content via the `content` slot (overrides `text`):
 *   <app-tooltip><i class="fa-info" /><template #content><b>Hi</b></template></app-tooltip>
 *
 * Renders through <c-teleport> to <body>, so it escapes any overflow:hidden /
 * transform-based clipping in the trigger's ancestry. jQuery-free replacement
 * for the legacy Bootstrap `data-toggle="tooltip"` pattern.
 */
export default {
  name: 'AppTooltip',

  props: {
    text: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'top',
      validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      visible: false,
      coords: { top: 0, left: 0, width: 0, height: 0 },
    }
  },

  computed: {
    // True when there's anything to show (plain text or a content slot).
    label() {
      return this.text || !!this.$slots.content
    },

    // Anchor point derived from the trigger rect; the paired CSS transform
    // (per placement) shifts the box so the correct edge meets the trigger.
    tipStyle() {
      const { top, left, width, height } = this.coords
      const gap = 9

      switch (this.placement) {
        case 'bottom':
          return { top: `${top + height + gap}px`, left: `${left + width / 2}px` }
        case 'left':
          return { top: `${top + height / 2}px`, left: `${left - gap}px` }
        case 'right':
          return { top: `${top + height / 2}px`, left: `${left + width + gap}px` }
        default: // top
          return { top: `${top - gap}px`, left: `${left + width / 2}px` }
      }
    },
  },

  beforeDestroy() {
    this.close()
  },

  methods: {
    open() {
      const rect = this.$el.getBoundingClientRect()
      this.coords = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }
      this.visible = true
    },

    close() {
      this.visible = false
    },
  },
}
</script>

<style lang="scss" scoped>
.app-tooltip__trigger {
  display: inline-flex;
  align-items: center;
}

.app-tooltip {
  position: fixed;
  z-index: 1080;
  padding: 5px 9px;
  background: #1f2937;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  pointer-events: none;

  // ── Placement offsets (paired with tipStyle anchor point) ──
  &--top    { transform: translate(-50%, -100%); }
  &--bottom { transform: translate(-50%, 0); }
  &--left   { transform: translate(-100%, -50%); }
  &--right  { transform: translate(0, -50%); }

  // ── Arrow: a 5px triangle on the edge facing the trigger ──
  &::after {
    content: '';
    position: absolute;
    border: 5px solid transparent;
  }

  &--top::after {
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: #1f2937;
  }
  &--bottom::after {
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: #1f2937;
  }
  &--left::after {
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-left-color: #1f2937;
  }
  &--right::after {
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-right-color: #1f2937;
  }
}

// Opacity-only fade — transform is reserved for placement positioning.
.app-tooltip-fade-enter-active,
.app-tooltip-fade-leave-active {
  transition: opacity 0.14s ease;
}
.app-tooltip-fade-enter,
.app-tooltip-fade-leave-to {
  opacity: 0;
}
</style>
